import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    TextField,
    MenuItem,
    Stack,
    Alert,
    CircularProgress,
    FormControlLabel,
    Switch,
    LinearProgress,
} from '@mui/material';
import { Close as CloseIcon, Send as SendIcon, Sync as SyncIcon, MailOutline as MailIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import EmailConfirmationDialog from './EmailConfirmationDialog';
import ExportZohoDialog from './ExportZohoDialog';
import InvoicePdfPreview from './InvoicePdfPreview';
import {
    fetchTeamEmailRecipients,
    getInvoiceById,
    fetchOrganizationDetails,
    generateInvoicePdfBlob,
    sendInvoiceEmail,
    sendInvoiceEmailToTeam,
    fetchPlanEmailRecipients,
    prepareBulkEmail
} from '../../../services/invoiceRecords.service';

/**
 * NEW Bulk Invoice Actions Modal
 * Allows performing controlled bulk updates, emails, and syncs
 * only when selected invoices are compatible.
 */
const BulkInvoiceActionsModal = ({
    open,
    onClose,
    selectedInvoices,
    onBulkUpdate,
    onBulkRaise,
    onBulkEmail,
    onBulkZohoSync,
    canCreate,
    canUpdate,
    canZohoSend,
}) => {
    const [status, setStatus] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [automated, setAutomated] = useState(false);
    const [applyAutomated, setApplyAutomated] = useState(false);

    // Dialog States
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailType, setEmailType] = useState('customer'); // 'customer' or 'team'
    const [emailDialogData, setEmailDialogData] = useState({
        toEmails: [],
        ccEmails: [],
        isLoading: false,
        error: null,
    });
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({
        total: 0,
        current: 0,
        completed: 0,
        inProgress: false,
        results: [],
        status: 'idle', // idle, preparing, sending, completed, cancelled
    });
    const [cancelSending, setCancelSending] = useState(false);

    const [zohoDialogOpen, setZohoDialogOpen] = useState(false);
    const [exportTargetIds, setExportTargetIds] = useState([]);

    const [loading, setLoading] = useState(false);

    // State for hidden invoice preview rendering
    const [invoiceForPreview, setInvoiceForPreview] = useState(null);
    const [orgDataForPreview, setOrgDataForPreview] = useState(null);

    // Compatibility Logic - matches backend validation
    const compatibility = useMemo(() => {
        if (!selectedInvoices?.length) return {};

        const rawStatuses = [...new Set(selectedInvoices.map(inv => (inv.status || inv.paymentStatus || '').toUpperCase()))];
        const sameStatus = rawStatuses.length === 1;

        const allToBeRaised = selectedInvoices.every(inv => {
            const s = (inv.status || inv.paymentStatus || '').toUpperCase();
            return s === 'TO_BE_RAISED' || inv.recordType === 'BILLING';
        });

        const automatedStates = [...new Set(selectedInvoices.map(inv => !!inv.automated))];
        const sameAutomated = automatedStates.length === 1;

        const emailSentStates = [...new Set(selectedInvoices.map(inv => !!inv.emailSent))];
        const sameEmailSent = emailSentStates.length === 1;

        const allAreInvoices = selectedInvoices.every(inv => inv.recordType === 'INVOICE');

        return {
            statusEdit: sameStatus && !allToBeRaised,
            dateEdit: allToBeRaised,
            automatedEdit: !allToBeRaised && sameAutomated, // Only allowed if all selected have SAME state
            emailCustomer: !selectedInvoices.some(inv => inv.emailSent || (inv.status || inv.paymentStatus || '').toUpperCase() === 'PAID') && allAreInvoices && !allToBeRaised,
            emailTeam: allAreInvoices && !allToBeRaised,
            syncZoho: allAreInvoices && sameStatus && !allToBeRaised && !selectedInvoices.some(inv => !!inv.zohoinvoicenumber || (inv.status || inv.paymentStatus || '').toUpperCase() === 'PAID'),
            toBeRaisedMode: allToBeRaised,
            sameStatusValue: sameStatus ? rawStatuses[0] : '',
            sameDateValue: (allToBeRaised && [...new Set(selectedInvoices.map(inv => inv.invoiceDate))].length === 1) ? selectedInvoices[0].invoiceDate : null,
            sameDueDateValue: (allToBeRaised && [...new Set(selectedInvoices.map(inv => inv.dueDate))].length === 1) ? selectedInvoices[0].dueDate : null,
            statusReason: allToBeRaised ? 'Status is locked to "To Be Raised" for billing records.' : (!sameStatus ? 'Status update requires all selected invoices to have the same current status.' : null),
            dateReason: !allToBeRaised ? 'Date updates are only allowed for "TO BE RAISED" invoices.' : null,
            automatedReason: allToBeRaised ? 'Automation settings are not available for billing records.' : (!sameAutomated ? 'Selected invoices have different Auto Reminder states. Please select invoices with the same status.' : null),
            actionReason: allToBeRaised ? 'Emails and Zoho sync are disabled for "To Be Raised" records.' : (selectedInvoices.some(inv => inv.emailSent) ? 'Some invoices already have emails sent.' : (selectedInvoices.some(inv => !!inv.zohoinvoicenumber) ? 'Some invoices are already synced with Zoho.' : (selectedInvoices.some(inv => (inv.status || inv.paymentStatus || '').toUpperCase() === 'PAID') ? 'Actions are restricted for PAID invoices.' : null))),
        };
    }, [selectedInvoices]);

    // Initialize fields with common values
    useEffect(() => {
        if (open && selectedInvoices?.length) {
            if (compatibility.sameStatusValue && !compatibility.toBeRaisedMode) {
                setStatus(compatibility.sameStatusValue);
            }
            if (compatibility.toBeRaisedMode) {
                setStatus('TO_BE_RAISED');
            }
            if (compatibility.sameDateValue) {
                setInvoiceDate(dayjs(compatibility.sameDateValue));
            }
            if (compatibility.sameDueDateValue) {
                setDueDate(dayjs(compatibility.sameDueDateValue));
            }

            // For automation, if all share the same state, show it. Otherwise default to false.
            const automatedStates = [...new Set(selectedInvoices.map(inv => !!inv.automated))];
            if (automatedStates.length === 1) {
                setAutomated(automatedStates[0]);
            } else {
                setAutomated(false);
            }
        }
    }, [open, selectedInvoices, compatibility]);

    const handleUpdate = async () => {
        const updates = {};
        if (compatibility.toBeRaisedMode) {
            updates.status = 'To Be Exported';
        } else if (status) {
            updates.status = status;
        }

        if (invoiceDate) updates.invoiceDate = invoiceDate.toISOString();
        if (dueDate) updates.dueDate = dueDate.toISOString();
        if (applyAutomated) updates.automated = automated;

        if (Object.keys(updates).length === 0) return;

        setLoading(true);
        try {
            if (compatibility.toBeRaisedMode) {
                await onBulkRaise({
                    invoiceIds: selectedInvoices.map(inv => inv.id || inv._id),
                    updates,
                });
            } else {
                await onBulkUpdate({
                    invoiceIds: selectedInvoices.map(inv => inv.id || inv._id),
                    updates,
                });
            }
            onClose();
        } catch (err) {
            console.error('Bulk update failed', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEmail = async (recipientType) => {
        setEmailType(recipientType);
        setEmailDialogOpen(true);
        setEmailDialogData({
            toEmails: [],
            ccEmails: [],
            isLoading: true,
            error: null,
        });

        try {
            if (recipientType === 'team') {
                const data = await fetchTeamEmailRecipients();
                setEmailDialogData({
                    toEmails: data?.to || [],
                    ccEmails: data?.cc || [],
                    isLoading: false,
                    error: null,
                });
            } else {
                // For bulk customer email, we don't fetch individual emails to display
                // just show count validation
                setEmailDialogData({
                    toEmails: [],
                    ccEmails: [],
                    isLoading: false,
                    error: null,
                });
            }
        } catch (error) {
            console.error('Error fetching email recipients:', error);
            setEmailDialogData(prev => ({ ...prev, isLoading: false, error: 'Failed to fetch recipients' }));
        }
    };

    const handleConfirmSendEmail = async () => {
        setIsSendingEmail(true);
        setLoading(true);
        setCancelSending(false);
        try {
            await performBulkSendEmail(emailType);
            // Don't close immediately if we want to show success state
            // setEmailDialogOpen(false);
            // onClose();
        } catch (err) {
            console.error('Bulk email failed', err);
        } finally {
            setIsSendingEmail(false);
            setLoading(false);
        }
    }

    const performBulkSendEmail = async (recipientType) => {
        const total = selectedInvoices.length;
        const batchId = `bulk_${Date.now()}`;

        setBulkProgress({
            total,
            current: 0,
            completed: 0,
            inProgress: true,
            results: [],
            status: 'preparing'
        });

        // 1. Prepare backend
        const inviteIds = selectedInvoices.map(inv => inv.id || inv._id);
        await prepareBulkEmail(inviteIds, batchId);

        setBulkProgress(prev => ({ ...prev, status: 'sending' }));

        console.log(`Starting client-side bulk email process for ${total} invoices (${recipientType})...`);

        let completedCount = 0;
        const results = [];

        for (let i = 0; i < selectedInvoices.length; i++) {
            // Check for cancellation
            if (cancelSending) {
                console.log('Bulk sending cancelled by user.');
                setBulkProgress(prev => ({ ...prev, status: 'cancelled', inProgress: false }));
                return;
            }

            const selectedInv = selectedInvoices[i];
            const invoiceId = selectedInv.id || selectedInv._id;

            setBulkProgress(prev => ({ ...prev, current: i + 1 }));

            try {
                // 1. Load full invoice details and organization details
                const invoiceResponse = await getInvoiceById(invoiceId);
                if (!invoiceResponse.success || !invoiceResponse.data) {
                    throw new Error(`Failed to load details for invoice ${invoiceId}`);
                }

                const invoice = invoiceResponse.data;
                let orgData = null;
                if (invoice.organizationId) {
                    orgData = await fetchOrganizationDetails(invoice.organizationId);
                }

                // 2. Set state to render hidden preview
                setInvoiceForPreview(invoice);
                setOrgDataForPreview(orgData);

                // 3. Wait for component to fully render
                await new Promise(resolve => setTimeout(resolve, 800));

                // 4. Find the preview element
                const previewElement = document.getElementById('bulk-invoice-pdf-preview');
                if (!previewElement) {
                    throw new Error(`Failed to render invoice preview for ${invoiceId}`);
                }

                // 5. Generate and send
                let result;
                if (recipientType === 'customer') {
                    const pdfBlob = await generateInvoicePdfBlob(previewElement, true);
                    result = await sendInvoiceEmail(invoiceId, 'customer', pdfBlob);
                } else {
                    result = await sendInvoiceEmailToTeam(invoiceId, 'team', previewElement);
                }

                if (result.success) {
                    completedCount++;
                    results.push({ id: invoiceId, success: true });
                    console.log(`Successfully sent ${recipientType} email for invoice ${invoiceId}`);
                } else {
                    results.push({ id: invoiceId, success: false, error: result.message });
                    console.error(`Failed to send ${recipientType} email for invoice ${invoiceId}: ${result.message}`);
                }

            } catch (err) {
                console.error(`Error processing invoice ${invoiceId}:`, err);
                results.push({ id: invoiceId, success: false, error: err.message });
            } finally {
                // Clean up preview state for next iteration
                setInvoiceForPreview(null);
                setOrgDataForPreview(null);
                setBulkProgress(prev => ({
                    ...prev,
                    completed: completedCount,
                    results: [...results]
                }));
            }
        }

        setBulkProgress(prev => ({
            ...prev,
            status: 'completed',
            inProgress: false
        }));
    }

    const handleSync = () => {
        setExportTargetIds(selectedInvoices.map(inv => inv.id || inv._id));
        setZohoDialogOpen(true);
    };

    const handleConfirmZohoExport = async () => {
        setZohoDialogOpen(false);
        setLoading(true);
        try {
            await onBulkZohoSync(selectedInvoices.map(inv => inv.id || inv._id));
            onClose();
        } catch (err) {
            console.error('Bulk Zoho sync failed', err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Bulk Invoice Actions ({selectedInvoices?.length})</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* Status Update */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom color="primary">Status Modification</Typography>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={!compatibility.statusEdit || loading}
                            label="Set Status To"
                        >
                            <MenuItem value="UNPAID">Unpaid</MenuItem>
                            <MenuItem value="PAID">Paid</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            {status === 'TO_BE_RAISED' && (
                                <MenuItem value="TO_BE_RAISED" disabled>To Be Raised</MenuItem>
                            )}
                        </TextField>
                    </Box>

                    {/* Date Updates */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom color="primary">Date Modification</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack direction="row" spacing={2}>
                                <DatePicker
                                    label="New Invoice Date"
                                    value={invoiceDate}
                                    onChange={setInvoiceDate}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    disabled={!compatibility.dateEdit || loading}
                                />
                                <DatePicker
                                    label="New Due Date"
                                    value={dueDate}
                                    onChange={setDueDate}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    disabled={!compatibility.dateEdit || loading}
                                />
                            </Stack>
                        </LocalizationProvider>
                    </Box>

                    {/* Payment Reminder */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom color="primary">Automation Settings</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={automated}
                                        onChange={(e) => {
                                            setAutomated(e.target.checked);
                                            setApplyAutomated(true);
                                        }}
                                        disabled={!compatibility.automatedEdit || loading}
                                    />
                                }
                                label="Enable Automated Payment Reminders"
                            />
                        </Stack>
                        {!compatibility.automatedEdit && (
                            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                                <Typography variant="caption">{compatibility.automatedReason}</Typography>
                            </Alert>
                        )}
                    </Box>

                    <Divider />

                    {/* Action Buttons */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom color="primary">Global Actions</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<SendIcon />}
                                fullWidth
                                onClick={() => handleEmail('team')}
                                disabled={!compatibility.emailTeam || loading || !canCreate}
                            >
                                Send to Team
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<MailIcon />}
                                fullWidth
                                onClick={() => handleEmail('customer')}
                                disabled={!compatibility.emailCustomer || loading || !canCreate}
                            >
                                Send to Customers
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<SyncIcon />}
                                fullWidth
                                onClick={handleSync}
                                disabled={!compatibility.syncZoho || loading || !canCreate || !canZohoSend}
                            >
                                Sync to Zoho
                            </Button>
                        </Stack>
                        {compatibility.actionReason && (
                            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                                <Typography variant="caption">{compatibility.actionReason}</Typography>
                            </Alert>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading}>Close</Button>
                <Button
                    variant="contained"
                    onClick={handleUpdate}
                    disabled={
                        loading ||
                        (!status && !invoiceDate && !dueDate && !applyAutomated) ||
                        (compatibility.toBeRaisedMode && !canCreate)
                    }
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    color="primary"
                >
                    {compatibility.toBeRaisedMode ? 'Apply Bulk Invoice Raising' : 'Apply Bulk Changes'}
                </Button>
            </DialogActions>

            {/* Email Confirmation Dialog */}
            <EmailConfirmationDialog
                open={emailDialogOpen}
                onClose={() => setEmailDialogOpen(false)}
                emailType={emailType}
                onConfirm={handleConfirmSendEmail}
                toEmails={emailDialogData.toEmails}
                ccEmails={emailDialogData.ccEmails}
                isLoading={emailDialogData.isLoading}
                error={emailDialogData.error}
                isSending={isSendingEmail}
                confirmationMessage={
                    bulkProgress.inProgress ? (
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Overall Progress: {bulkProgress.completed}/{bulkProgress.total} sent
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {Math.round((bulkProgress.completed / bulkProgress.total) * 100)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(bulkProgress.completed / bulkProgress.total) * 100}
                                sx={{
                                    height: 12,
                                    borderRadius: 5,
                                    backgroundColor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 5,
                                        backgroundColor: (bulkProgress.completed / bulkProgress.total) > 0.8 ? '#4caf50' : '#2196f3',
                                    }
                                }}
                            />
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                                {bulkProgress.status === 'preparing' ? 'Preparing data...' : `Processing invoice ${bulkProgress.current} of ${bulkProgress.total}...`}
                            </Typography>
                            {bulkProgress.status === 'completed' && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    All emails processed! {bulkProgress.completed} succeeded, {bulkProgress.total - bulkProgress.completed} failed.
                                </Alert>
                            )}
                            {bulkProgress.status === 'cancelled' && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Process cancelled by user.
                                </Alert>
                            )}
                        </Box>
                    ) : (
                        emailType === 'team'
                            ? `Are you sure you want to send ${selectedInvoices.length} invoice(s) to the team?`
                            : `Are you sure you want to send ${selectedInvoices.length} invoice(s) to the customer(s)?`
                    )
                }
                onCancelProcess={() => setCancelSending(true)}
            />

            {/* Zoho Export Confirmation */}
            <ExportZohoDialog
                open={zohoDialogOpen}
                onClose={() => setZohoDialogOpen(false)}
                onConfirm={handleConfirmZohoExport}
                plantName=""
                invoiceNumber="" // Not showing single invoice details for bulk
                count={selectedInvoices.length}
            />

            {/* Hidden invoice preview for PDF generation */}
            {invoiceForPreview && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '-10000px',
                        top: 0,
                        width: '800px',
                        opacity: 0,
                        pointerEvents: 'none',
                        zIndex: -1,
                    }}
                >
                    <InvoicePdfPreview
                        invoice={invoiceForPreview}
                        organizationData={orgDataForPreview}
                        onClose={() => { }}
                        id="bulk-invoice-pdf-preview"
                    />
                </Box>
            )}
        </Dialog>
    );
};

export default BulkInvoiceActionsModal;
